import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { BreakString } from 'foundations-components/transitional/components/BreakString';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Image } from 'foundations-components/transitional/components/Image';
import { Lightbox } from 'foundations-components/transitional/components/Lightbox';
import { Section } from 'foundations-components/transitional/components/Section';
import { View } from 'foundations-components/transitional/components/View';
import { useFocusCloseButton } from 'marketplace-ui-common/hooks/useFocusCloseButton';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { FeatureCompatibilityMessage } from '../../compatibility/FeatureCompatibilityMessage';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { useFeatureCompatibilities } from '../../hooks/useFeatureCompatibilities';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function Feature({
  children,
  feature,
  onImageClick,
  index
}) {
  const [showLightBox, setShowLightBox] = useState(false);
  useFocusCloseButton(showLightBox);
  const {
    featureName,
    featureDescription,
    featureImage
  } = feature;
  const renderedLightbox = useMemo(() => showLightBox && featureImage.value && /*#__PURE__*/_jsx(Lightbox, {
    imageAlt: featureImage.altText,
    imageSrc: featureImage.value,
    onClose: () => setShowLightBox(false),
    "data-test-id": "feature-lightbox",
    children: /*#__PURE__*/_jsx(ButtonWrapper, {
      children: featureDescription
    })
  }), [featureImage, featureDescription, showLightBox, setShowLightBox]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Section, {
      marginBlockEnd: "600",
      children: /*#__PURE__*/_jsxs(Grid, {
        rowGap: "200",
        children: [featureImage.value && /*#__PURE__*/_jsx(GridItem, {
          size: {
            xs: 12,
            lg: 5
          },
          children: /*#__PURE__*/_jsx(Clickable, {
            onClick: () => {
              setShowLightBox(true);
              if (onImageClick) {
                onImageClick();
              }
            },
            children: /*#__PURE__*/_jsx(Image, {
              alt: featureImage.altText,
              src: featureImage.value,
              responsive: true,
              shape: "rounded",
              inlineSize: "100%",
              "data-test-id": index === 0 ? 'first-feature-image' : 'feature-image'
            })
          })
        }), /*#__PURE__*/_jsxs(GridItem, {
          size: {
            sm: 12,
            lg: featureImage.value ? 7 : 12
          },
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "heading-100",
            children: /*#__PURE__*/_jsx(BreakString, {
              children: featureName
            })
          }), /*#__PURE__*/_jsx(View, {
            marginBlock: "200",
            children: children
          }), /*#__PURE__*/_jsx(Text, {
            whiteSpace: "pre-line",
            children: /*#__PURE__*/_jsx(BreakString, {
              children: featureDescription
            })
          })]
        })]
      })
    }, `feature-${featureName}`), renderedLightbox]
  });
}
export function ProductFeaturesCard({
  features,
  integrationFeatures = [],
  onImageClick,
  onCompatibilityModalOpen,
  showCompatibility = false
}) {
  const {
    appContext,
    trackInteraction,
    productType
  } = useDetailsPageContext();
  const integrationFeaturesAsFeatures = integrationFeatures.map(integrationFeature => ({
    // put the deprecated integrationFeature
    // into the shape of a UnifiedDetailsFeature
    featureName: integrationFeature.headline,
    featureDescription: integrationFeature.text,
    featureImage: {
      value: integrationFeature.imageUrl,
      altText: integrationFeature.headline
    }
  }));
  const {
    result: featureCompatibilitiesData,
    loading: featureCompatibilitiesLoading,
    error: featureCompatibilitiesError
  } = useFeatureCompatibilities({
    appContext,
    features,
    productType
  });
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product features card'
    }, eventProps));
  }, [trackInteraction]);
  const renderedFeatures = useMemo(() => {
    if (features !== null && features !== void 0 && features.length) {
      const filteredFeatures = productType !== 'APP' ? features //skip check for featureLocation for non-apps
      : features.filter(feature => {
        var _feature$featureLocat, _feature$featureLocat2;
        return (_feature$featureLocat = feature === null || feature === void 0 || (_feature$featureLocat2 = feature.featureLocation) === null || _feature$featureLocat2 === void 0 ? void 0 : _feature$featureLocat2.includes('MARKETPLACE_LISTING')) !== null && _feature$featureLocat !== void 0 ? _feature$featureLocat : false;
      });
      return filteredFeatures.map((feature, index) => {
        var _compatibilityData$sa;
        const compatibilityData = featureCompatibilitiesData[feature.featureName];
        const isCompatible = (_compatibilityData$sa = compatibilityData === null || compatibilityData === void 0 ? void 0 : compatibilityData.satisfied) !== null && _compatibilityData$sa !== void 0 ? _compatibilityData$sa : false;
        return /*#__PURE__*/_jsx(Feature, {
          feature: feature,
          onImageClick: onImageClick,
          index: index,
          children: showCompatibility && !featureCompatibilitiesError ? /*#__PURE__*/_jsx(FeatureCompatibilityMessage, {
            isCompatible: isCompatible,
            onCompatibilityModalOpen: onCompatibilityModalOpen,
            onTrackInteraction: handleTrackInteraction
          }) : undefined
        }, feature.featureName);
      });
    } else {
      return integrationFeaturesAsFeatures.map((feature, index) => /*#__PURE__*/_jsx(Feature, {
        feature: feature,
        onImageClick: onImageClick,
        index: index
      }, feature.featureName));
    }
  }, [featureCompatibilitiesData, featureCompatibilitiesError, features, onImageClick, productType, showCompatibility, onCompatibilityModalOpen, handleTrackInteraction, integrationFeaturesAsFeatures]);
  if (!renderedFeatures.length || featureCompatibilitiesLoading) {
    return null;
  }
  return /*#__PURE__*/_jsx(DetailsPageCard, {
    id: "product-features-card",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.featuresCard.title"
    }),
    actions: showCompatibility ? /*#__PURE__*/_jsx(Button, {
      use: "link",
      onClick: onCompatibilityModalOpen,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.featuresCard.compare"
      })
    }) : undefined,
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      children: renderedFeatures
    })
  });
}